import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  isMobile,
  useTranslation,
  useClickOutside,
  parseNumberByPrecision,
} from '@pascal/pascal-core';
import { freeBetStore, notificationStore } from '@pascal/pascal-core/shared/core/store';

import { gameStore } from '../../store/gameStore';
import { doBetStore } from '../../store/doBetStore';
import { autoBetStore } from '../../store/autoBetStore';
import { maxOddValue, MIN_ODD } from '../../enums/constants';
import { TranslationKeys } from '../../enums/translationKeys';
import { betResponseCodeByName } from '../../enums/betResponseCode';
import { useAmountValidation } from '../../hooks/useAmountValidation';
import { oddInputPlusOrMinus } from '../../enums/oddInputPlusOrMinus';

const WinChanceCounter = observer(({ numPadOdd = '', setNumPadOdd, numPadOnChange }) => {
  const t = useTranslation();
  const { isPlay } = autoBetStore;
  const { isBetsClosed, isOpenNumPad, setIsOpenNumPad, isFreeBetActivatedFromToggle } = gameStore;
  const { selectedOdd, setSelectedOdd } = doBetStore;
  const { activeFreeBet } = freeBetStore;
  const [oddValue, setOddValue] = useState(null);
  const [chance, setChance] = useState(null);
  const [oddInputRef, hasOddValueInputClickedOutSide] = useClickOutside();
  const { onChange: handleChangeOddValue } = useAmountValidation(setOddValue);

  useEffect(() => {
    // eslint-disable-next-line no-nested-ternary
    const odd = isMobile() ? (isOpenNumPad ? numPadOdd : oddValue) : oddValue;
    if (odd >= MIN_ODD && odd <= maxOddValue) {
      setChance(parseNumberByPrecision(1, (0.96 / +odd) * 100, 3));
    } else {
      setChance(0);
    }
  }, [oddValue, isOpenNumPad, numPadOdd]);

  useEffect(() => {
    if (hasOddValueInputClickedOutSide && !isFreeBetActivatedFromToggle) {
      if (!activeFreeBet.id) {
        if (+oddValue < MIN_ODD) {
          setTimeout(() => {
            setOddValue(MIN_ODD);
            notificationStore.setNotificationType(betResponseCodeByName.InvalidMinOdd);
            setTimeout(() => notificationStore.setNotificationType(null), 1000);
          }, 200);
        }
      }

      if (activeFreeBet.id) {
        if (+oddValue < activeFreeBet.minOdd) {
          setTimeout(() => {
            setOddValue(activeFreeBet.minOdd);
            notificationStore.setNotificationType(betResponseCodeByName.InvalidFreeBetMinOdd);
            setTimeout(() => notificationStore.setNotificationType(null), 1000);
          }, 200);
        }
      }
      if (+oddValue > maxOddValue) {
        setTimeout(() => {
          setOddValue(maxOddValue);
          notificationStore.setNotificationType(betResponseCodeByName.InvalidMaxOdd);
          setTimeout(() => notificationStore.setNotificationType(null), 1000);
        }, 200);
      }
      setSelectedOdd(oddValue);
    }
  }, [
    hasOddValueInputClickedOutSide,
    oddValue,
    setSelectedOdd,
    activeFreeBet.id,
    isFreeBetActivatedFromToggle,
  ]);

  useEffect(() => {
    if (isFreeBetActivatedFromToggle && activeFreeBet) {
      setOddValue(activeFreeBet.minOdd);
      gameStore.setIsFreeBetActivatedFromToggle(false);
    }
  }, [isFreeBetActivatedFromToggle]);

  useEffect(() => {
    if (!selectedOdd) {
      setTimeout(() => {
        setSelectedOdd(MIN_ODD);
        notificationStore.setNotificationType(betResponseCodeByName.InvalidMinOdd);
        setTimeout(() => notificationStore.setNotificationType(null), 1000);
      }, 200);
    } else {
      setOddValue(selectedOdd);
    }
  }, [selectedOdd]);

  useEffect(() => {
    if (oddValue) {
      setSelectedOdd(oddValue);
    }
  }, [oddValue, setSelectedOdd]);

  const handleInputClick = useCallback(
    (evt) => {
      evt.stopPropagation();
      if (!isOpenNumPad) {
        setIsOpenNumPad(true);
      }
    },
    [isOpenNumPad, setIsOpenNumPad]
  );

  const handlePlusOrMinusOddValue = (action) => {
    let changedOddValue;
    // eslint-disable-next-line no-nested-ternary
    const odd = isMobile() ? (isOpenNumPad ? numPadOdd : oddValue) : oddValue;
    if (action === oddInputPlusOrMinus.minus) {
      changedOddValue = (+odd - 0.1).toFixed(2);
      changedOddValue = changedOddValue > MIN_ODD ? changedOddValue : MIN_ODD;
    } else {
      changedOddValue = (+odd + 0.1).toFixed(2);
    }
    const validOdd = parseNumberByPrecision(1, changedOddValue, 2).toString();
    // eslint-disable-next-line no-nested-ternary
    isMobile()
      ? isOpenNumPad
        ? setNumPadOdd(validOdd)
        : setOddValue(validOdd)
      : setOddValue(validOdd);
  };

  return (
    <>
      <div className='action-labels-row'>
        <p className='action-label'>{t(TranslationKeys.TargetMultiplier)} (x1.01 - x10,000)</p>
        <p className='action-label'>{t(TranslationKeys.WinChance)} %</p>
      </div>
      <div
        ref={oddInputRef}
        className={`action-field-group ${isPlay || isBetsClosed ? 'disabled' : ''}`}
      >
        <div className='action-field-count-block'>
          <button
            type='button'
            className='icon-minus'
            aria-label='minus'
            disabled={oddValue <= MIN_ODD || (activeFreeBet.id && activeFreeBet.minOdd >= oddValue)}
            onClick={(evt) => {
              evt.stopPropagation();
              handlePlusOrMinusOddValue(oddInputPlusOrMinus.minus);
            }}
          />
          <input
            type='text'
            readOnly={!!isMobile()}
            maxLength={7}
            value={isMobile() ? (isOpenNumPad ? numPadOdd : oddValue) || '' : oddValue || ''}
            onChange={numPadOnChange || handleChangeOddValue}
            onClick={(evt) => handleInputClick(evt)}
          />
          <button
            type='button'
            className='icon-plus'
            aria-label='plus'
            disabled={oddValue >= maxOddValue}
            onClick={(evt) => {
              evt.stopPropagation();
              handlePlusOrMinusOddValue();
            }}
          />
        </div>
        <input readOnly type='text' value={chance || chance === 0 ? `${chance}%` : ''} />
      </div>
    </>
  );
});

export default WinChanceCounter;
